import { createStoreSlice, type SetField } from 'features/store/zustand';
import {
  getIsBulkWritingFeedbackEnabled,
  getIsCwfCanvasEnabled,
  getIsCwfGlowsGrowsEnabled,
  getIsCwfGoogleClassroomEnabled,
  getIsCwfGoogleDriveEnabled,
  getIsCwfGradeEnabled,
  getIsCwfNextStepsEnabled,
  getIsCwfOneDriveEnabled,
  getIsCwfPdfDownloadEnabled,
  getIsCwfSchoologyEnabled,
  getIsImageGenEnabled,
  getIsKnowledgeEnabled,
  getIsLomPurchased,
  getIsMagicStudentEnabled,
  getIsMagicStudentToggleLocked,
  getIsMcpConnectorEnabled,
  getIsQuizzesEnabled,
  getIsStudentExportStudentThreadsEnabled,
  getIsTeacherExportStudentThreadsEnabled,
} from './actions';
import type { ToolAccessFlags } from './fetchToolAccessFlags';

export type ToolAccessStoreLoadOptions = {
  orgId: string;
  magicStudent?: boolean;
  mcpConnector?: boolean;
  bulkWritingFeedback?: boolean;
  knowledge?: boolean;
  quizzes?: boolean;
  exportStudentThreads?: boolean;
  lomPurchased?: boolean;
  imageGen?: boolean;
};

export type ToggleLockStoreField = 'isStudentToggleLocked' | 'isQuizzesToggleLocked' | 'isBulkWritingFeedbackToggleLocked';

export type ToolAccessStore = {
  loading: boolean;
  isStudentEnabled: boolean | null;
  isStudentToggleLocked: boolean | null;
  isQuizzesToggleLocked: boolean | null;
  isBulkWritingFeedbackToggleLocked: boolean | null;
  isMcpConnectorEnabled: boolean | null;
  isBulkWritingFeedbackEnabled: boolean | null;
  isQuizzesEnabled: boolean | null;
  isLomPurchased: boolean | null;
  isCwfGradeEnabled: boolean | null;
  isCwfGlowsGrowsEnabled: boolean | null;
  isCwfNextStepsEnabled: boolean | null;
  isCwfGoogleClassroomEnabled: boolean | null;
  isCwfGoogleDriveEnabled: boolean | null;
  isCwfOneDriveEnabled: boolean | null;
  isCwfCanvasEnabled: boolean | null;
  isCwfSchoologyEnabled: boolean | null;
  isCwfPdfDownloadEnabled: boolean | null;
  isKnowledgeEnabled: boolean | null;
  isTeacherExportStudentThreadsEnabled: boolean | null;
  isStudentExportStudentThreadsEnabled: boolean | null;
  isImageGenEnabled: boolean | null;
  hydrate: (flags: ToolAccessFlags) => void;
  load: (options?: ToolAccessStoreLoadOptions) => Promise<void>;
  reset: () => void;
  setField: SetField<ToolAccessStore>;
};

const defaultState = {
  loading: true,
  isStudentEnabled: null,
  isStudentToggleLocked: null,
  isQuizzesToggleLocked: null,
  isBulkWritingFeedbackToggleLocked: null,
  isMcpConnectorEnabled: null,
  isBulkWritingFeedbackEnabled: null,
  isQuizzesEnabled: null,
  isLomPurchased: null,
  isCwfGradeEnabled: null,
  isCwfGlowsGrowsEnabled: null,
  isCwfNextStepsEnabled: null,
  isCwfGoogleClassroomEnabled: null,
  isCwfGoogleDriveEnabled: null,
  isCwfOneDriveEnabled: null,
  isCwfCanvasEnabled: null,
  isCwfSchoologyEnabled: null,
  isCwfPdfDownloadEnabled: null,
  isKnowledgeEnabled: null,
  isTeacherExportStudentThreadsEnabled: null,
  isStudentExportStudentThreadsEnabled: null,
  isImageGenEnabled: null,
};

const resolveFlag = (response: { error?: unknown; data?: boolean }) =>
  response?.error !== undefined || response?.data === undefined ? null : response.data;

export const createToolAccessStoreSlice = createStoreSlice('ToolAccessStoreData', defaultState, ({ set, setField }) => ({
  setField,
  hydrate: (flags) => set({ ...flags, loading: false }),
  load: async (options) => {
    const {
      orgId = '',
      // configures this store to conditionally load each enabled flag
      magicStudent: initializeMagicStudent = true,
      mcpConnector: initializeMcpConnector = true,
      bulkWritingFeedback: initializeBulkWritingFeedback = true,
      knowledge: initializeKnowledge = true,
      quizzes: initializeQuizzes = true,
      exportStudentThreads = true,
      lomPurchased: initializeLomPurchased = true,
      imageGen: initializeImageGen = true,
    } = options || {};

    set({ loading: true });

    const [
      studentResponse,
      studentToggleResponse,
      mcpConnectorResponse,
      bulkWritingFeedbackResponse,
      cwfGradeResponse,
      cwfGlowsGrowsResponse,
      cwfNextStepsResponse,
      cwfGoogleClassroomResponse,
      cwfGoogleDriveResponse,
      cwfOneDriveResponse,
      cwfCanvasResponse,
      cwfSchoologyResponse,
      cwfPdfDownloadResponse,
      knowledgeResponse,
      quizzesResponse,
      teacherExportResponse,
      studentExportResponse,
      lomPurchasedResponse,
      imageGenResponse,
    ] = await Promise.all([
      initializeMagicStudent ? getIsMagicStudentEnabled(orgId) : undefined,
      initializeMagicStudent ? getIsMagicStudentToggleLocked(orgId) : undefined,
      initializeMcpConnector ? getIsMcpConnectorEnabled(orgId) : undefined,
      initializeBulkWritingFeedback ? getIsBulkWritingFeedbackEnabled(orgId) : undefined,
      initializeBulkWritingFeedback ? getIsCwfGradeEnabled(orgId) : undefined,
      initializeBulkWritingFeedback ? getIsCwfGlowsGrowsEnabled(orgId) : undefined,
      initializeBulkWritingFeedback ? getIsCwfNextStepsEnabled(orgId) : undefined,
      initializeBulkWritingFeedback ? getIsCwfGoogleClassroomEnabled(orgId) : undefined,
      initializeBulkWritingFeedback ? getIsCwfGoogleDriveEnabled(orgId) : undefined,
      initializeBulkWritingFeedback ? getIsCwfOneDriveEnabled(orgId) : undefined,
      initializeBulkWritingFeedback ? getIsCwfCanvasEnabled(orgId) : undefined,
      initializeBulkWritingFeedback ? getIsCwfSchoologyEnabled(orgId) : undefined,
      initializeBulkWritingFeedback ? getIsCwfPdfDownloadEnabled(orgId) : undefined,
      initializeKnowledge ? getIsKnowledgeEnabled(orgId) : undefined,
      initializeQuizzes ? getIsQuizzesEnabled(orgId) : undefined,
      exportStudentThreads ? getIsTeacherExportStudentThreadsEnabled(orgId) : undefined,
      exportStudentThreads ? getIsStudentExportStudentThreadsEnabled(orgId) : undefined,
      initializeLomPurchased ? getIsLomPurchased(orgId) : undefined,
      initializeImageGen ? getIsImageGenEnabled(orgId) : undefined,
    ]);

    if (studentResponse) set({ isStudentEnabled: resolveFlag(studentResponse) });
    if (studentToggleResponse) set({ isStudentToggleLocked: resolveFlag(studentToggleResponse) });
    if (mcpConnectorResponse) set({ isMcpConnectorEnabled: resolveFlag(mcpConnectorResponse) });
    if (bulkWritingFeedbackResponse) set({ isBulkWritingFeedbackEnabled: resolveFlag(bulkWritingFeedbackResponse) });
    if (cwfGradeResponse) set({ isCwfGradeEnabled: resolveFlag(cwfGradeResponse) });
    if (cwfGlowsGrowsResponse) set({ isCwfGlowsGrowsEnabled: resolveFlag(cwfGlowsGrowsResponse) });
    if (cwfNextStepsResponse) set({ isCwfNextStepsEnabled: resolveFlag(cwfNextStepsResponse) });
    if (cwfGoogleClassroomResponse) set({ isCwfGoogleClassroomEnabled: resolveFlag(cwfGoogleClassroomResponse) });
    if (cwfGoogleDriveResponse) set({ isCwfGoogleDriveEnabled: resolveFlag(cwfGoogleDriveResponse) });
    if (cwfOneDriveResponse) set({ isCwfOneDriveEnabled: resolveFlag(cwfOneDriveResponse) });
    if (cwfCanvasResponse) set({ isCwfCanvasEnabled: resolveFlag(cwfCanvasResponse) });
    if (cwfSchoologyResponse) set({ isCwfSchoologyEnabled: resolveFlag(cwfSchoologyResponse) });
    if (cwfPdfDownloadResponse) set({ isCwfPdfDownloadEnabled: resolveFlag(cwfPdfDownloadResponse) });
    if (knowledgeResponse) set({ isKnowledgeEnabled: resolveFlag(knowledgeResponse) });
    if (quizzesResponse) set({ isQuizzesEnabled: resolveFlag(quizzesResponse) });
    if (teacherExportResponse) set({ isTeacherExportStudentThreadsEnabled: resolveFlag(teacherExportResponse) });
    if (studentExportResponse) set({ isStudentExportStudentThreadsEnabled: resolveFlag(studentExportResponse) });
    if (lomPurchasedResponse) set({ isLomPurchased: resolveFlag(lomPurchasedResponse) });
    if (imageGenResponse) set({ isImageGenEnabled: resolveFlag(imageGenResponse) });

    set({ loading: false });
  },
  reset: () => set({ ...structuredClone(defaultState) }),
}));
